import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useMatch } from 'react-router-dom';
import { useSelector } from 'react-redux';
import _debounce from 'lodash/debounce';

import { watchBanner } from 'components/Banner';
import { closeModal } from 'components/Modal';
import { watchSmartSpot } from 'components/SmartSpot';
import { watchSnackbar } from 'components/Snackbar';
import { venuePropTypes } from 'constants/propTypes';
import AppLayout from 'features/App/AppLayoutContainer';
import { redirectIfAuthed, watchAuthActions } from 'features/Auth/Auth.saga';
import { watchSidenav } from 'features/Sidenav';
import { watchSmartSpotBannerActions } from 'features/SmartSpotBanner';
import { userHasAccessToVenue } from 'helpers/active-venue';
import { trackPageView } from 'helpers/events';
import { IA_KEY, iaLogic } from 'helpers/ia-logic';
import { useBeforeMount } from 'helpers/react-hooks';
import { redirectToLoginOnce } from 'helpers/session-auth-redirect';
import { setRouteParams, setRouteQuery } from 'reducers/route';
import { selectForceRefreshOnRouteChange } from 'selectors/app';
import { selectRouteParams } from 'selectors/general';
import { selectIsMobile } from 'selectors/ui';

import { handleRootRouteEnter, redirectUnauthenticated, redirectUnpermissioned } from './App.saga';
import PageLayout from './PageLayoutContainer';

const PAGE_VIEW_DEBOUNCE_LENGTH = 500;
const trackPageViewWithDebounce = _debounce(trackPageView, PAGE_VIEW_DEBOUNCE_LENGTH, {
  trailing: true,
});
const EMPTY_ROUTE_PARAMS = {};

const getIaKey = (iaKeyProp, params) => {
  return typeof iaKeyProp === 'function' ? iaKeyProp(params) : iaKeyProp;
};

const getHideVenueSelectorValue = ({ hideVenueSelector, hideVenueSelectorConditional, routeContext }) => {
  if (!hideVenueSelector) return false;
  if (typeof hideVenueSelectorConditional !== 'function') return true;
  return Boolean(hideVenueSelectorConditional(routeContext));
};

const UnwrappedRoute = props => {
  const {
    component,
    componentProps = {},
    hasMultipleColumns = false,
    hideNav,
    hideVenueSelector = false,
    hideVenueSelectorConditional,
    iaKey: iaKeyFromProps,
    isFixedPage = false,
    isPublic = false,
    locationSagas = [],
    pageLayout = 'wide',
    path,
    pathSagas = [],
    redirectIfAuthenticated = false,
    store,
    userInfo,
    validateParams,
  } = props;

  const location = useLocation();
  const isMobile = useSelector(selectIsMobile);

  // set route props in store on route enter or location change (same path, different params)
  const match = useMatch(path);
  const routeParams = match?.params || EMPTY_ROUTE_PARAMS;
  const routeContext = { params: routeParams, userInfo, location, isMobile };
  const hideVenueSelectorValue = getHideVenueSelectorValue({
    hideVenueSelector,
    hideVenueSelectorConditional,
    routeContext,
  });

  // If the URL targets a venueId the user doesn't have access to, redirect before the page renders.
  // Page selectors (e.g. selectIsAppointmentTypeVenue) destructure activeVenue synchronously and would
  // otherwise throw on undefined fields, tripping the error boundary before the API's 403 returns.
  const urlVenueId = routeParams.venueId;
  const blockedByVenueAccess =
    !isPublic &&
    !redirectIfAuthenticated &&
    Boolean(urlVenueId) &&
    userInfo &&
    Array.isArray(userInfo.venuesList) &&
    !userHasAccessToVenue(userInfo, urlVenueId);

  useEffect(() => {
    if (blockedByVenueAccess) {
      redirectToLoginOnce(403);
    }
  }, [blockedByVenueAccess]);

  // iaKey can either be a static key or a function that returns a key based on the params
  const iaKey = getIaKey(iaKeyFromProps, routeParams);

  // remove tabs, query params, and hashes from the path to create clean categories
  const pathCategory = (typeof path === 'string' ? path : path[0])
    .split('/:tab')[0]
    .split('/:routeTab')[0]
    .replace(/(\?.*)|(#.*)/g, '');

  useBeforeMount(() => {
    const params = validateParams ? validateParams(routeParams) : routeParams;

    store.dispatch(setRouteParams(params));
    store.dispatch(setRouteQuery(location.search));
  }, [location.search, routeParams, validateParams]);

  // autorun all the sagas that need to trigger once on PATH changes (route enter (before this Route + Layout renders)), BUT NOT whenever LOCATION changes
  // also do all auth and permissions stuff
  // autostop all the started sagas on route leave (when this Route + Layout unmounts)
  useEffect(() => {
    const sagasToRun = pathSagas.slice(0);

    if (!isPublic && path !== '/') {
      sagasToRun.unshift(redirectUnauthenticated);
    }

    if (redirectIfAuthenticated) {
      sagasToRun.unshift(redirectIfAuthed);
      sagasToRun.unshift(watchAuthActions);
    }

    if (iaKey) {
      const { userHasAccess } = iaLogic;

      // don't run any sagas for the route/feature if the user doesn't have access
      if (!userHasAccess(iaKey)(userInfo)) {
        store.runSaga(redirectUnpermissioned, store.sagaDependencies);
        return () => {
          store.stopSaga(redirectUnpermissioned);
        };
      }
    }

    // Add all app level sagas. watchApp is started at store bootstrap (see store/create.js)
    // so SESSION_AUTH_EXPIRED has a listener before any route mounts.
    sagasToRun.unshift(
      handleRootRouteEnter,
      watchBanner,
      watchSidenav,
      watchSmartSpot,
      watchSmartSpotBannerActions,
      watchSnackbar
    );

    // Run all sagas, deduping and teardown handled by the store
    store.runSagas('path', sagasToRun, store.sagaDependencies, iaKey);

    // stops page level sagas, allowing app level sagas to continue
    return () => store.stopSagas('path', pathSagas);
  }, [iaKey, pathSagas, store, userInfo]);

  // autorun all the sagas that need to trigger once on PATH changes (route enter (before this Route + Layout renders)), AND whenever LOCATION changes
  // autostop all the started sagas on location leave (when this Route + Layout unmounts)
  useEffect(() => {
    const { userHasAccess } = iaLogic;
    if (!iaKey || userHasAccess(iaKey)(userInfo)) {
      store.runSagas('location', locationSagas, store.sagaDependencies, iaKey);

      return () => store.stopSagas('location', locationSagas);
    }
  }, [store, locationSagas, iaKey, location, userInfo]);

  // on every location change (but NOT query param or hash change), fire page view event
  // TODO: some pages' tabs do not count as multiple page views and require a refactor (Opportunities)
  useEffect(() => {
    const state = store.getState();
    const { routeTab, tab } = selectRouteParams(state);
    trackPageViewWithDebounce({
      pagePathCategory: pathCategory,
      path: location?.pathname,
      queryString: location?.search,
      tabName: tab || routeTab,
    });
  }, [path, location?.pathname]);

  // force refresh on route leave (ie, when this instance of Route + Layout unmounts)
  // TODO only refresh when going into a different route, not when going deeper or sideways in the current route
  useEffect(() => {
    const state = store.getState();
    const forceRefresh = selectForceRefreshOnRouteChange(state);
    if (forceRefresh) window.location.reload(true);
  }, [store]);

  // autoclose modal on route leave (ie, when this instance of Route + Layout unmounts)
  useEffect(() => store.dispatch.bind(store, closeModal()), [store]);

  if (blockedByVenueAccess) {
    return null;
  }

  return (
    <AppLayout hideNav={hideNav}>
      <PageLayout
        isPublic={isPublic}
        layoutInfo={{
          hasMultipleColumns,
          hideVenueSelector: hideVenueSelectorValue,
          isFixedPage,
          pageLayout: isPublic ? 'public' : pageLayout,
        }}>
        {component ? <props.component {...componentProps} /> : null}
      </PageLayout>
    </AppLayout>
  );
};

UnwrappedRoute.propTypes = {
  component: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.func]),
  componentProps: PropTypes.shape({}), // any/everything is valid
  hasMultipleColumns: PropTypes.bool,
  hideNav: PropTypes.bool,
  hideVenueSelector: PropTypes.bool,
  hideVenueSelectorConditional: PropTypes.func,
  iaKey: PropTypes.oneOfType([PropTypes.oneOf(Object.values(IA_KEY)), PropTypes.func]),
  isFixedPage: PropTypes.bool,
  isPublic: PropTypes.bool,
  locationSagas: PropTypes.arrayOf(PropTypes.func),
  pageLayout: PropTypes.oneOf(['wide', 'narrow']),
  path: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  pathSagas: PropTypes.arrayOf(PropTypes.func),
  redirectIfAuthenticated: PropTypes.bool,
  store: PropTypes.shape({
    dispatch: PropTypes.func,
    getState: PropTypes.func,
    sagaDependencies: PropTypes.shape({}),
    stopSagas: PropTypes.func,
    runSagas: PropTypes.func,
    stopSaga: PropTypes.func,
    runSaga: PropTypes.func,
  }),
  userInfo: PropTypes.shape({
    userId: PropTypes.number,
    venuesList: PropTypes.arrayOf(PropTypes.shape(venuePropTypes)),
  }),
  validateParams: PropTypes.func,
};

export default UnwrappedRoute;
